import { CustomerContact, ProCustomer } from '../types/pro'

export function newContactId(): string {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
}

export function emptyContact(primary = false): CustomerContact {
  return {
    id: newContactId(),
    name: '',
    role: '',
    phone: '',
    email: '',
    primary,
    notes: '',
  }
}

/**
 * Every customer reads as a list of contacts, including the ones saved before
 * this existed — their single contact person, phone and email become the
 * first entry so nothing has to be migrated in the database.
 */
export function contactsOf(customer: ProCustomer): CustomerContact[] {
  const saved = customer.contacts ?? []
  if (saved.length > 0) return saved

  const name = (customer.contactPerson ?? '').trim()
  if (!name && !customer.phone && !customer.email) return []

  return [
    {
      id: 'primary',
      name: name || customer.name,
      role: customer.type === 'firma' ? 'Kontaktperson' : '',
      phone: customer.phone,
      email: customer.email,
      primary: true,
      notes: '',
    },
  ]
}

/** The one to write to unless the user picked someone else. */
export function primaryContact(
  customer: ProCustomer,
): CustomerContact | undefined {
  const list = contactsOf(customer)
  return list.find((contact) => contact.primary) ?? list[0]
}

/** Exactly one contact may be primary; the first one wins if none is set. */
export function normalizeContacts(
  contacts: CustomerContact[],
): CustomerContact[] {
  const cleaned = contacts.filter(
    (contact) =>
      contact.name.trim() || contact.phone.trim() || contact.email.trim(),
  )
  if (cleaned.length === 0) return []
  if (cleaned.some((contact) => contact.primary)) {
    let seen = false
    return cleaned.map((contact) => {
      if (contact.primary && !seen) {
        seen = true
        return contact
      }
      return { ...contact, primary: false }
    })
  }
  return cleaned.map((contact, index) => ({ ...contact, primary: index === 0 }))
}

/** "Lene Bech (Driftsleder)" — used in pickers and summaries. */
export function contactLabel(contact: CustomerContact): string {
  return contact.role ? `${contact.name} (${contact.role})` : contact.name
}

/** Everything searchable about a customer's people. */
export function contactSearchText(customer: ProCustomer): string {
  return contactsOf(customer)
    .flatMap((contact) => [contact.name, contact.role, contact.phone, contact.email])
    .filter(Boolean)
    .join(' ')
}
